import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, Plus, Search, Trash2, Edit3, Eye, EyeOff, Copy, ShieldAlert, ExternalLink } from "lucide-react";

const empty = { site_name: "", url: "", username: "", password: "", note: "", category: "pharmacy" };

export default function Credentials() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/vault${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load vault"));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    try {
      if (row.id) await api.put(`/vault/${row.id}`, row);
      else await api.post("/vault", row);
      toast.success(row.id ? "Credential updated" : "Credential added");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this credential? This cannot be undone.")) return;
    try { await api.delete(`/vault/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2" data-testid="vault-header">
            <Lock className="w-6 h-6 text-pink-800" /> Password Vault
          </h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Encrypted AES-256 at rest. Passwords are masked by default — every "Reveal" is written to the audit log.
          </p>
        </div>
        <Button data-testid="vault-new-btn" onClick={() => setEditing({ ...empty })}
          className="bg-pink-700 hover:bg-pink-800 gap-1.5">
          <Plus className="w-4 h-4" /> Add Credential
        </Button>
      </div>

      <Card className="p-3 mb-4 border-amber-200 bg-amber-50 flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900">
          <b>Sensitive:</b> Only administrators can see this page. Passwords are encrypted at rest,
          never transmitted in list responses, and every reveal request is logged with the actor's
          email, timestamp, IP, and target site.
        </div>
      </Card>

      <Card className="p-3 border-slate-200 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="vault-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search site, url, username…" className="pl-8 h-9 text-sm" />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-x-auto">
        <Table data-testid="vault-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Site</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Password</TableHead>
              <TableHead>Last rotated</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">
                Vault is empty.</TableCell></TableRow>
            ) : rows.map((r) => (
              <VaultRow key={r.id} row={r} onEdit={() => setEditing(r)} onDelete={() => del(r.id)} />
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && <CredentialDialog row={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function VaultRow({ row, onEdit, onDelete }) {
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(false);

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    if (!confirm(`Reveal the password for "${row.site_name}"?\n\nThis action will be audit-logged.`)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/vault/${row.id}/reveal`);
      setRevealed(data.password || "");
      toast.success("Revealed (logged)");
      setTimeout(() => setRevealed(null), 30_000);
    } catch (e) { toast.error(e.response?.data?.detail || "Reveal failed"); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    if (!revealed) return toast.error("Reveal first");
    try { await navigator.clipboard.writeText(revealed); toast.success("Copied to clipboard"); }
    catch { toast.error("Clipboard blocked"); }
  };

  return (
    <TableRow data-testid={`vault-row-${row.id}`}>
      <TableCell>
        <div className="font-medium text-slate-800">{row.site_name}</div>
        {row.url && (
          <a href={row.url.startsWith("http") ? row.url : `https://${row.url}`}
            target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-pink-800 inline-flex items-center gap-1 mt-0.5">
            <ExternalLink className="w-3 h-3" /> {row.url}
          </a>
        )}
        {row.note && <div className="text-[11px] text-slate-500 mt-0.5">{row.note}</div>}
      </TableCell>
      <TableCell className="font-mono text-xs">{row.username || <span className="text-slate-400">—</span>}</TableCell>
      <TableCell className="font-mono text-xs">
        {revealed
          ? <span data-testid={`vault-revealed-${row.id}`} className="bg-amber-100 px-2 py-0.5 rounded">{revealed}</span>
          : <span className="text-slate-400">{row.password_masked || "••••••••"}</span>}
      </TableCell>
      <TableCell className="text-xs text-slate-500 font-mono">
        {row.password_last_updated ? row.password_last_updated.slice(0, 10) : "—"}
      </TableCell>
      <TableCell className="text-right">
        <button onClick={reveal} disabled={loading}
          data-testid={`vault-reveal-${row.id}`}
          className="text-slate-500 hover:text-pink-800 mr-1.5" title={revealed ? "Hide" : "Reveal (audited)"}>
          {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button onClick={copy} disabled={!revealed}
          data-testid={`vault-copy-${row.id}`}
          className="text-slate-500 hover:text-pink-800 mr-1.5 disabled:opacity-30" title="Copy revealed">
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={onEdit} data-testid={`vault-edit-${row.id}`}
          className="text-slate-500 hover:text-pink-800 mr-1.5"><Edit3 className="w-4 h-4" /></button>
        <button onClick={onDelete} data-testid={`vault-del-${row.id}`}
          className="text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
      </TableCell>
    </TableRow>
  );
}

function CredentialDialog({ row, onClose, onSave }) {
  const [f, setF] = useState({ ...row, password: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isEdit = !!row.id;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Credential" : "Add Credential"}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Field label="Site name *" v={f.site_name} onChange={(v) => set("site_name", v)} testid="v-site" />
          <Field label="URL" v={f.url} onChange={(v) => set("url", v)} testid="v-url" placeholder="https://…" />
          <Field label="Username" v={f.username} onChange={(v) => set("username", v)} testid="v-user" />
          <div>
            <label className="text-[10px] text-slate-500 uppercase">
              Password {isEdit ? "(leave blank to keep existing)" : "*"}
            </label>
            <Input data-testid="v-pw" type="password" value={f.password || ""}
              onChange={(e) => set("password", e.target.value)}
              className="h-9 text-sm font-mono"
              autoComplete="new-password" />
          </div>
          <Field label="Notes" v={f.note} onChange={(v) => set("note", v)} testid="v-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="v-save" onClick={() => onSave(f)}
            className="bg-pink-700 hover:bg-pink-800">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, v, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] text-slate-500 uppercase">{label}</label>
    <Input data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className="h-9 text-sm" />
  </div>
);
